import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { UrlService } from '../url/url.service';
import {
	ACCOUNT_DETAIL_FIELDS,
	ACCOUNT_SETTINGS_FIELD,
	AccountDetailsView,
	AccountSettings,
	AdminLanesTable,
	Bid,
	BID_STATUS_UUID,
	BidDetailsTable,
	BidViewTemplate,
	CALCULATE_TRANSIT_DAYS,
	CarrierNotes,
	CLIENT_PROFILE_ROWS,
	ColumnFields,
	COLUMNS_VIEW,
	DATE_MDY,
	DATE_YMD,
	IS_EMPTY_LIST,
	IS_NOT_EMPTY,
	IS_NOT_NULL_OR_UNDEFINED,
	LOCATION_PARSER,
	OAuthClients,
	Quote,
	QUOTE_STATUS_STRING,
	QuoteViewTemplate,
	RestService,
	SnackbarService,
	TIMESTAMP_FROM_STRING,
} from 'cargo-chief-commons';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { UtilService } from '../util/util.service';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { SegmentWrapperService } from '@service/segmentWrapper/segment-wrapper.service';
import { CustomRestService } from '@service/customRest/custom-rest.service';
import { AccountSettingsService } from '@service/accountSettings/account-settings.service';
import { CacheService } from '../cache/cache.service';
import { map, tap } from 'rxjs/operators';
import { SegmentEventsConstants } from '@domain/constants/segment-events-constants';
import { environment } from '@environment/environment';
import { BID_ACTION_TEXT, QUALITY_SCORE_DISPLAY } from '@domain/constants/app-variables';
import { IpService } from '@service/ip/ip.service';

@Injectable({
	providedIn: 'root',
})
export class SharedService {
	closeFullProfileSidenav = new BehaviorSubject<boolean>(null);
	private bidActivitySubject = new BehaviorSubject(null);
	private isFullWidthLayout = new BehaviorSubject(null);
	private searchCompletedSubject = new BehaviorSubject(null);
	private carrierSearchDropdownSubject = new BehaviorSubject<any>(null);
	private carrierAssigneeSubject = new BehaviorSubject<any>(null);
	private closeCarrierFullProfile = new BehaviorSubject<any>(null);
	private showFeedbackNotification = new BehaviorSubject<any>(null);
	private feedbackNotificationClicked = new BehaviorSubject<any>(null);
	private carrierUploadedCount = new BehaviorSubject<number>(null);
	private customerRoleRemoved = new BehaviorSubject<boolean>(null);
	private addNewProjectCarrierMapContact = new BehaviorSubject<any>(null);

	constructor(
		private urlService: UrlService,
		private restService: RestService,
		private snackbar: SnackbarService,
		private utilService: UtilService,
		private cargochief: Cargochief,
		private segmentWrapper: SegmentWrapperService,
		private customRestService: CustomRestService,
		public accountSettings: AccountSettingsService,
		private cacheService: CacheService,
		private ipService: IpService
	) {}

	buildObjectForBidNotification(payload) {
		return {
			bidId: payload['bid.id'],
			bidHistorySourceType: payload['bidHistory.sourceType'],
			bidHistoryAmount: payload['bidHistory.amount'],
			bidStatus: BID_STATUS_UUID[payload['bid.status']],
			bidPickupDate:
				payload['bid.pickupDate'] != undefined && payload['bid.pickupDate'] != null
					? TIMESTAMP_FROM_STRING(payload['bid.pickupDate'])
					: null,
			bidDeliveryDate:
				payload['bid.deliveryDate'] != undefined && payload['bid.deliveryDate'] != null
					? TIMESTAMP_FROM_STRING(payload['bid.deliveryDate'])
					: null,
			loadId: payload['load.id'],
			declineReason:
				payload['bid.declineReason'] != undefined && payload['bid.declineReason'] != null
					? payload['bid.declineReason']
					: '',
			notificationName: payload['notification.name'],
		};
	}

	submitBid(
		action: string,
		payload: any,
		segmentName: string,
		bid: Bid,
		quote: Quote,
		isBrokerCounter: boolean,
		showErrorSnackbar: boolean = true
	): Observable<any> {
		let url = this.urlService.postBidUrl(bid.id);
		payload.isBrokerCounter = isBrokerCounter;
		payload.action = action;
		// skipMcDotValidation is true only for logged-in carriers (we skip MC/DOT validation)
		const isCarrierLoggedIn = Boolean(payload['skipMcDotValidation']);
		payload.isCarrierLoggedIn = isCarrierLoggedIn;
		let restService: any = this.customRestService;
		if (this.cargochief.USER_ID) {
			restService = this.restService;
		}
		return restService.POST(url, payload).pipe(
			tap(
				async (result) => {
					let ipAddress = null;
					try {
						ipAddress = await this.ipService.getIpAddress();
					} catch {}
					this.segmentWrapper.fireSegmentEvent(
						segmentName,
						this.bidSegmentPropertiesPayload(bid, quote, action, segmentName, payload, ipAddress)
					);
					return result;
				},
				(error) => {
					let errorMessage = 'Unable to complete your request now. Please try again later.';
					if (error['data']) {
						errorMessage = error['data'];
					}
					if (showErrorSnackbar) {
						this.utilService.showSnackbar(errorMessage, SNACKBAR.ERROR, SNACKBAR.ACTION);
					}
				}
			)
		);
	}

	bidSegmentPropertiesPayload(
		bid: Bid,
		quote: Quote,
		responseType: string,
		segmentName: string,
		requestPayload: any,
		ipAddress: string
	) {
		const user = {
			id: bid?.bidHistories?.find((bidHistory) => IS_NOT_NULL_OR_UNDEFINED(bidHistory.sourceId))
				?.sourceId,
		};
		let result = {};
		if (
			bid.isEligibleForBookNow &&
			segmentName === SegmentEventsConstants.carrier_countered_from_book_now
		) {
			let offerChanges = [],
				pickupDate = DATE_MDY(bid.pickupDate),
				deliveryDate = DATE_MDY(bid.deliveryDate);
			if (requestPayload.amount !== bid.allInPrice) {
				offerChanges.push({
					attribute: 'allInPrice',
					original_value: bid.allInPrice,
					changed_value: requestPayload.amount,
				});
			}
			if (DATE_MDY(requestPayload.pickupDate) !== pickupDate) {
				offerChanges.push({
					attribute: 'pickupDate',
					original_value: pickupDate,
					changed_value: DATE_MDY(requestPayload.pickupDate),
				});
			}
			if (DATE_MDY(requestPayload.deliveryDate) !== deliveryDate) {
				offerChanges.push({
					attribute: 'deliveryDate',
					original_value: deliveryDate,
					changed_value: DATE_MDY(requestPayload.deliveryDate),
				});
			}
			if (offerChanges.length) {
				result['changes'] = offerChanges;
			}
			result['bidId'] = bid.id;
			result['carrierId'] = bid.carrierId;
		} else {
			result = {
				carrierId: bid.carrierId,
				businessName: quote.businessName,
				bid: bid,
				demo_account: this.accountSettings.DEMO_ACCOUNT,
				response_type: responseType,
				user: user,
			};
		}
		result['user'] = {
			accountId: quote.accountId,
			businessName: quote.businessName,
			email: quote.userEmail,
			id: quote.userId,
			firstName: quote.userName,
		};
		result['isCarrierLoggedIn'] = requestPayload?.isCarrierLoggedIn;
		result['ipAddress'] = ipAddress;
		return result;
	}

	getBidActionView(quote: Quote, copyToClipboard: boolean = false): Array<Array<ColumnFields>> {
		const view = COLUMNS_VIEW(
			Object.keys(BidViewTemplate).map((field) => {
				return BidViewTemplate[field];
			}),
			[
				LOCATION_PARSER(quote.origin),
				LOCATION_PARSER(quote.destination),
				quote.equipment,
				quote.totalWeight != undefined && quote.totalWeight != null ? quote.totalWeight : 0,
				quote.approximateMiles,
				quote.productDescription != undefined && quote.productDescription != null
					? quote.productDescription
					: '',
				DATE_MDY(quote.pickupDate),
				DATE_MDY(quote.estimatedDeliveryDate),
				quote.loadNumber,
				quote.businessName,
				quote.specialInstructions != undefined && quote.specialInstructions != null
					? quote.specialInstructions
					: '',
				quote.desiredPrice != undefined && quote.desiredPrice != null ? quote.desiredPrice : 0,
			],
			[],
			[
				'string',
				'string',
				'string',
				'number',
				'number',
				'string',
				'date',
				'date',
				'string',
				'string',
				'specialInstructions',
				'currency',
			],
			[
				'',
				'',
				'',
				' lbs',
				'',
				'',
				!this.utilService.isNullOrUndefined(quote.pickupOpenTime) &&
				!this.utilService.isNullOrUndefined(quote.pickupCloseTime)
					? quote.pickupOpenTime.slice(0, 5) + '-' + quote.pickupCloseTime.slice(0, 5)
					: '',
				!this.utilService.isNullOrUndefined(quote.deliveryOpenTime) &&
				!this.utilService.isNullOrUndefined(quote.deliveryCloseTime)
					? quote.deliveryOpenTime.slice(0, 5) + '-' + quote.deliveryCloseTime.slice(0, 5)
					: '',
				'',
				'',
				'',
				'',
				'',
			],
			['p-8'],
			['f-s-w-500 primary-text'],
			['f-s'],
			[],
			copyToClipboard ? ['end center'] : []
		);
		let result: Array<Array<ColumnFields>> = [];
		view.map((column) => {
			result.push([column]);
		});
		if (copyToClipboard) {
			result.splice(8, 2);
			let businessName: ColumnFields = {} as ColumnFields;
			businessName.labelText = `${quote.businessName}`;
			businessName.labelValue = `${quote.loadNumber}`;
			businessName.icon = 'content_copy';
			businessName.valueType = `copy`;
			businessName.rowClass = 'p-8';
			businessName.labelTextClass = 'f-s-w-500 primary-text';
			businessName.labelValueClass = 'f-s';
			businessName.iconClass = 'primary-text';
			businessName.labelValueFlexLayout = 'end center';
			result.unshift([businessName]);
		}
		return result;
	}

	getQuoteDetailsView(quote: Quote): Array<ColumnFields> {
		const result = COLUMNS_VIEW(
			Object.keys(QuoteViewTemplate).map((field) => {
				return QuoteViewTemplate[field];
			}),
			[
				LOCATION_PARSER(quote.origin),
				LOCATION_PARSER(quote.destination),
				quote.equipment,
				quote.productDescription,
				DATE_MDY(quote.pickupDate),
				DATE_MDY(quote.estimatedDeliveryDate),
				quote.totalWeight,
				quote.referenceNumber,
				quote.revenueCode,
				quote.desiredPrice,
				quote.orderType,
			],
			[
				'place',
				'place',
				'local_shipping',
				'info',
				'date_range',
				'date_range',
				'info',
				'info',
				'text_snippet',
				'attach_money',
				'label',
			],
			[
				'location',
				'location',
				'string',
				'string',
				'date',
				'date',
				'number',
				'string',
				'string',
				'currency',
				'string',
			],
			['', '', '', '', '', '', ' lbs', '', '', '', ''],
			['padding-0-5 font-size-16'],
			['textWithIcon'],
			['label-text-light'],
			['primary-light-color'],
			['end center']
		);
		return result;
	}

	oAuthClientProfileView(client: OAuthClients): Array<ColumnFields> {
		const result = COLUMNS_VIEW(
			Object.keys(CLIENT_PROFILE_ROWS).map((field) => {
				return CLIENT_PROFILE_ROWS[field];
			}),
			[
				client.clientId,
				client.resourceIds,
				client.clientSecret,
				client.scope,
				client.authorizedGrantTypes,
				client.webServerRedirectUri,
				client.authorities,
				client.accessTokenValidity,
				client.refreshTokenValidity,
				client.additionalInformation,
				client.autoapprove,
			],
			[],
			[
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
			],
			[],
			['padding-0-5 font-size-16'],
			[],
			['label-text-light'],
			[]
		);
		return result;
	}

	adminAccountDetails(details: AccountDetailsView): Array<ColumnFields> {
		const result = COLUMNS_VIEW(
			Object.keys(ACCOUNT_DETAIL_FIELDS)
				.map((field) => {
					return ACCOUNT_DETAIL_FIELDS[field];
				})
				.concat(' '),
			[
				details.accountId,
				details.dot,
				details.plan,
				details.name,
				details.mc,
				details.createdAt,
				details.fromEmail,
				details.daysToPay != null ? details.daysToPay : '',
				details.systemName,
				details.APIToken,
				' ',
			],
			[],
			[
				'string',
				'string',
				'updatePlan',
				'string',
				'string',
				'date',
				'string',
				'string',
				'string',
				'longString',
				'string',
			],
			[],
			['padding-0-5 font-size-16'],
			['label-text-light'],
			[],
			[]
		);
		return result;
	}

	adminAccountSettings(
		accountSettings: AccountSettings,
		valueTextLayout: string
	): Array<ColumnFields> {
		const result = COLUMNS_VIEW(
			Object.keys(ACCOUNT_SETTINGS_FIELD).map((field) => {
				return ACCOUNT_SETTINGS_FIELD[field];
			}),
			[
				accountSettings.SEARCH_TRUCK_PICKUP_DATE_RANGE,
				accountSettings.SEARCH_TRUCK_ORIGIN_DEADHEAD_LIMIT,
				accountSettings.SEARCH_TRUCK_RESULTS_COUNT,
				accountSettings.SEARCH_TRUCK_DESTINATION_DEADHEAD_LIMIT,
				accountSettings.DEMO_ACCOUNT,
				accountSettings.BCC_USER_OFFERS,
				accountSettings.SEARCHABLE_TRAILER_TYPES,
				accountSettings.PICK_UP_APPOINTMENT_REQUIRED,
				accountSettings.DECLARED_VALUE_REQUIRED,
				accountSettings.DELIVERY_APPOINTMENT_REQUIRED,
				accountSettings.SEND_RATE_CON,
				accountSettings.IS_FEEDBACK_ENABLED,
				accountSettings.TRUCK_SEARCH_ENABLED,
				accountSettings.FREQUENCY_ENABLED,
				accountSettings.WEIGHT_REQUIRED,
				accountSettings.TARGET_MARGIN == 'null' || !IS_NOT_EMPTY(accountSettings.TARGET_MARGIN)
					? 'N/A'
					: accountSettings.TARGET_MARGIN,
				accountSettings.PROJECT_PROBABILITY,
				accountSettings.DEFAULT_TO_MY_PROJECTS,
				accountSettings.ANNUAL_VOLUME,
				accountSettings.SCORECARDS,
				accountSettings.CUSTOMERS,
				accountSettings.CARRIER_RATES,
				accountSettings.POST_LOAD,
				accountSettings.VOLUME_REQUIRED,
				accountSettings.PROJECTS_BOARD,
				accountSettings.USER_ENABLED_PROJECT_BOARD,
				accountSettings.MCLEOD_INTEGRATION,
				accountSettings.HAZMAT,
				accountSettings.STRONG_LANES,
				accountSettings.TEAMS_CAPACITY,
				accountSettings.BOOKING_ASSISTANT,
				// CN-8994: paired here because COLUMNS_VIEW matches labelValue[index] to each label
				// positionally — a label with no value slot shifts every row after it and blanks the
				// last (the CN-8712 note below). Missing reads as 'true': absent means allowed.
				accountSettings.BOOKING_ASSISTANT_AUTOMATION_ALLOWED === 'false' ? 'false' : 'true',
				accountSettings.GET_OPTION_VISIBLE,
				accountSettings.CUSTOMIZABLE_EMAILS,
				accountSettings.EMAIL_ROUTING,
				accountSettings.CONFIDENCE_SCORE,
				accountSettings.SEND_LOADS_WITH_NO_RATE,
				accountSettings.RATE_RECENCY_LIMIT,
				accountSettings.PICKUP_DATE_THRESHOLD,
				accountSettings.PARTIAL_LOAD_ENABLED,
				accountSettings.AMAZON_FREIGHT,
				accountSettings.ROLE_BASED_CUSTOMER_DATA_ACCESS,
				accountSettings.BA_PERFORMANCE_METRICS_ENABLED,
				accountSettings.CARRIER_QUALITY,
				accountSettings.ENABLE_OUT_OF_NETWORK_CARRIERS,
				accountSettings.CAPACITY_DATA_IN_RFP,
				// Accounts have no BULK_RATING_ENABLED row until an admin first enables it, so a
				// missing flag renders 'false' rather than blank (same treatment as
				// PROCUREMENT_VERSIONS / DASHBOARD_VERSIONS below).
				accountSettings.BULK_RATING_ENABLED === 'true' ? 'true' : 'false',
				accountSettings.AUTO_INVITE_CARRIERS,
				accountSettings.USE_RECENT_LANE_SEARCHES,
				accountSettings.SHOW_LOADS_PREDICTED_PRICE,
				accountSettings.INCLUDE_DISPATCHER_IN_BOOK_NOW_OFFER,
				accountSettings.HIDE_CUSTOMER_DETAILS_FROM_NON_ADMIN_USERS,
				accountSettings.NON_ADMIN_SEE_OWN_LOADS_ONLY,
				accountSettings.LANE_ANALYTICS,
				accountSettings.LANE_CARRIER_ANALYTICS,
				accountSettings.WORKFLOW_PERFORMANCE_REPORT,
				accountSettings.TOP_CARRIERS_ENABLED,
				// CN-8712: these two labels sit at the tail of ACCOUNT_SETTINGS_FIELD but
				// had no value slot (COLUMNS_VIEW pairs labelValue[index] to each label),
				// so they rendered blank. Pair them and treat a missing flag as 'false'.
				accountSettings.PROCUREMENT_VERSIONS === 'true' ? 'true' : 'false',
				accountSettings.DASHBOARD_VERSIONS === 'true' ? 'true' : 'false',
			],
			[],
			[
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				'string',
				// CN-8861: one extra 'string' for the added BULK_RATING_ENABLED value. The
				// flag block from CAPACITY_DATA_IN_RFP onward is all 'string', so the type
				// array stays aligned with the label/value arrays.
				'string',
			],
			[],
			['padding-0-5 font-size-16'],
			['label-text-light'],
			[],
			[],
			[valueTextLayout]
		);
		return result;
	}

	laneDetails(lane: AdminLanesTable): Array<ColumnFields> {
		const result = COLUMNS_VIEW(
			['Origin:', 'Destination States:', 'Trailer Type:', 'Lane Count:'],
			[
				lane.originCity + ', ' + lane.originState,
				lane.destinationStates,
				lane.trailerType,
				lane.laneCount,
			],
			[],
			['string', 'string', 'string', 'string'],
			[],
			['padding-0-5 font-size-16'],
			['label-text-light'],
			[],
			[]
		);
		return result;
	}

	updateAPIToken(accountId: number, roles: string) {
		const url = this.urlService.getAPIToken(accountId);
		this.restService.GET(url).subscribe(
			(apiToken) => {
				if (apiToken === '') {
					this.generateToken(accountId, roles);
				} else {
					this.removeToken(accountId);
				}
			},
			(error) => {
				this.utilService.showSnackbar('Unable to get API token.', SNACKBAR.ERROR, SNACKBAR.ACTION);
			}
		);
	}

	generateToken(accountId: number, roles: string): Observable<any> {
		const url = this.urlService.generateToken(accountId, roles);
		return this.restService.GET(url).pipe(
			map(
				(result) => {
					this.utilService.showSnackbar(
						'API enabled successfully.',
						SNACKBAR.SUCCESS,
						SNACKBAR.ACTION
					);
					this.setAPIToken(accountId, result.body.token);
					return result.body.token;
				},
				(error) => {
					this.utilService.showSnackbar('Enable API failed.', SNACKBAR.ERROR, SNACKBAR.ACTION);
				}
			)
		);
	}

	removeToken(accountId: number): Observable<any> {
		const url = this.urlService.removeToken(accountId);
		return this.restService.POST(url, {}).pipe(
			map(
				(result) => {
					if (result) {
						this.utilService.showSnackbar(
							'API disabled successfully.',
							SNACKBAR.SUCCESS,
							SNACKBAR.ACTION
						);
						this.evictToken(accountId);
						return null;
					}
				},
				(error) => {
					this.utilService.showSnackbar(
						'Unable to disable API token.',
						SNACKBAR.ERROR,
						SNACKBAR.ACTION
					);
				}
			)
		);
	}

	setAPIToken(accountId: number, token: string) {
		const url = this.urlService.setApiToken(accountId);
		// The token goes in the body, never the URL — c4 logs request URIs at INFO.
		this.restService.POST(url, { apiToken: token }).subscribe(
			(apiToken) => {},
			(error) => {
				this.utilService.showSnackbar('Set API token api failed.', SNACKBAR.ERROR, SNACKBAR.ACTION);
			}
		);
	}

	evictToken(accountId: number) {
		const url = this.urlService.tokenEvict(accountId);
		this.restService.GET(url).subscribe(
			(apiToken) => {},
			(error) => {
				this.utilService.showSnackbar('Enable to evict token.', SNACKBAR.ERROR, SNACKBAR.ACTION);
			}
		);
	}

	getBookingConfirmationColumns(bid: BidDetailsTable, quote: Quote): Array<ColumnFields> {
		const result = COLUMNS_VIEW(
			[
				'Origin:',
				'Destination:',
				'Trailer Type:',
				'Weight (lbs):',
				'Approximate Miles:',
				'Commodity:',
				'Expected Pickup:',
				'Expected Delivery:',
				'Team Service Required:',
				'Special Instructions:',
			],
			[
				LOCATION_PARSER(quote.origin),
				LOCATION_PARSER(quote.destination),
				typeof quote.equipment === 'string'
					? quote.equipment
					: Array.isArray(quote.equipment) && (quote.equipment as any[]).length > 0
						? (quote.equipment as any[])[0]['name']
						: '--',
				quote.totalWeight ?? quote['weight'] ?? '--',
				quote.approximateMiles,
				quote.productDescription != undefined &&
				quote.productDescription != null &&
				quote.productDescription.length > 0
					? quote.productDescription
					: 'None',
				bid.pickupDate,
				bid.deliveryDate,
				quote.teamServiceRequired ? 'Yes' : 'No',
				quote.specialInstructions,
			],
			[],
			[
				'string',
				'string',
				'string',
				'number',
				'number',
				'string',
				'date',
				'date',
				'string',
				'specialInstructions',
			],
			['', '', '', ' lbs', '', '', '', '', '', ''],
			['padding-0-5'],
			['font-size-18 primary-text'],
			['font-size-16'],
			[]
		);
		return result;
	}

	sendBidActivity(data: any) {
		this.bidActivitySubject.next(data);
	}

	getBidActivityUpdate() {
		return this.bidActivitySubject.asObservable();
	}

	sendSearchCompletedEvent(data: any) {
		this.searchCompletedSubject.next(data);
	}

	getSearchCompletedEvent(): Observable<any> {
		return this.searchCompletedSubject.asObservable();
	}

	sendLayoutToggled(data) {
		this.isFullWidthLayout.next(data);
	}

	getLayoutToggledValue() {
		return this.isFullWidthLayout.asObservable();
	}

	sendCarrierSearchEvent(data: any) {
		this.carrierSearchDropdownSubject.next(data);
	}

	getCarrierSearchEvent(): Observable<CarrierNotes> {
		return this.carrierSearchDropdownSubject.asObservable();
	}

	getCarrierAssignedEvent(): Observable<CarrierNotes> {
		return this.carrierAssigneeSubject.asObservable();
	}

	sendCarrierAssignedEvent(data: any) {
		this.cacheService.getUserCarrierMaps();
		this.carrierAssigneeSubject.next(data);
	}

	updateUserCCarrierMap(data, userCarrierMap: any): any {
		if (data.deleted) {
			delete userCarrierMap[data.carrierId];
		} else if (!data.deleted) {
			userCarrierMap[data.carrierId] = data.assigneeName;
		}
		return userCarrierMap;
	}

	getCarrierFullProfileEvent(): Observable<CarrierNotes> {
		return this.closeCarrierFullProfile.asObservable();
	}

	sendCarrierFullProfileEvent(data: any) {
		this.closeCarrierFullProfile.next(data);
	}

	getfeedbackNotificationEvent(): Observable<any> {
		return this.showFeedbackNotification.asObservable();
	}

	sendfeedbackNotificationEvent(data: any) {
		this.showFeedbackNotification.next(data);
	}

	getfeedbackNotificationClickedEvent(): Observable<any> {
		return this.feedbackNotificationClicked.asObservable();
	}

	sendfeedbackNotificationClickedEvent(data: any) {
		this.feedbackNotificationClicked.next(data);
	}

	setDyForStatsSVG(className: string) {
		setTimeout(() => {
			let element = document.querySelector(`.${className} text tspan:last-of-type`);
			if (IS_NOT_NULL_OR_UNDEFINED(element)) {
				element.setAttribute('dy', '3.2em');
			}
		}, 300);
	}

	getCarrierUploadedCountEvent(): Observable<any> {
		return this.carrierUploadedCount.asObservable();
	}

	sendCarrierUploadedCountEvent(data: number) {
		this.carrierUploadedCount.next(data);
	}

	getCustomerRoleRemovedEvent(): Observable<any> {
		return this.customerRoleRemoved.asObservable();
	}

	sendCustomerRoleRemovedEvent(data: any) {
		this.customerRoleRemoved.next(data);
	}

	getOfferPayload(bid: Bid, bidAction, counterAmount, mcOrDot, skipMcDotValidation) {
		if (bidAction == BID_ACTION_TEXT.ACCEPT || bidAction == BID_ACTION_TEXT.BOOK_NOW) {
			return { mcOrDot, skipMcDotValidation };
		}
		return {
			amount: counterAmount * 100,
			estimatedDays: Number(CALCULATE_TRANSIT_DAYS(bid.pickupDate, bid.deliveryDate)),
			notes: '',
			pickupDate: DATE_YMD(bid.pickupDate),
			deliveryDate: DATE_YMD(bid.deliveryDate),
			mcOrDot,
			skipMcDotValidation,
		};
	}

	getOfferAction(bidAction) {
		if (bidAction == BID_ACTION_TEXT.ACCEPT || bidAction == BID_ACTION_TEXT.BOOK_NOW) {
			return BID_ACTION_TEXT.ACCEPT.toLowerCase();
		} else {
			return BID_ACTION_TEXT.COUNTER.toLowerCase();
		}
	}

	getOfferSegmentEventType(bid: Bid, bidAction) {
		if (bidAction == BID_ACTION_TEXT.ACCEPT || bidAction == BID_ACTION_TEXT.BOOK_NOW) {
			return SegmentEventsConstants.carrier_responded_to_offer;
		} else {
			return !bid.isEligibleForBookNow
				? SegmentEventsConstants.carrier_responded_to_offer
				: SegmentEventsConstants.carrier_countered_from_book_now;
		}
	}

	isEditableBidStatus(bid: Bid): boolean {
		return (
			bid.status === 'NEW' ||
			bid.status === 'INVITED' ||
			bid.status === 'VIEWED' ||
			bid.status === 'RESPONSE_RECEIVED'
		);
	}

	isLastBidByCustomer(bid: Bid) {
		if (IS_EMPTY_LIST(bid.bidHistories)) {
			return true;
		}
		const bidHistories = [...bid.bidHistories].sort((a, b) => (b.created > a.created ? 1 : -1));
		const sourceType = bidHistories[0].sourceType;
		if (
			!this.utilService.isNullOrUndefined(bid.bidHistories) &&
			bid.bidHistories.length > 0 &&
			sourceType == 'CUSTOMER'
		) {
			return true;
		} else {
			return false;
		}
	}

	isNotEditableBidStatus(bid: Bid): boolean {
		return (
			bid.status === 'EXPIRED' ||
			bid.status === 'CANCELLED' ||
			bid.status === 'DECLINED' ||
			bid.status === 'ACCEPTED' ||
			bid.status === 'SELECTED'
		);
	}

	isQuoteStatus(quote) {
		return quote?.quoteStatus?.toLowerCase() === QUOTE_STATUS_STRING.FILLED.toLowerCase();
	}

	getCarrierScoreToDisplay(score, carrierActive, forBrokerQualityData: boolean = false) {
		if (!score || score === 'N/A') {
			if (forBrokerQualityData || !carrierActive) {
				return QUALITY_SCORE_DISPLAY.LOW;
			}
		}
		if (Number.isInteger(score)) {
			return score > environment.HIGH_QUALITY
				? QUALITY_SCORE_DISPLAY.HIGH
				: score > environment.MEDIUM_QUALITY
					? QUALITY_SCORE_DISPLAY.MEDIUM
					: QUALITY_SCORE_DISPLAY.LOW;
		}
	}

	getAddNewProjectCarrierMapContactEvent(): Observable<any> {
		return this.addNewProjectCarrierMapContact.asObservable();
	}

	sendAddNewProjectCarrierMapContactEvent(data: any) {
		this.addNewProjectCarrierMapContact.next(data);
	}
}
